'use client';

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useResponsivePaneDialog(onClose: () => void, initialFocusRef: RefObject<HTMLElement | null>, active = true) {
  const paneRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [isMobileOverlay, setIsMobileOverlay] = useState(false);

  useEffect(() => {
    if (!active) return;
    const query = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 767px)') : null;
    const sync = () => setIsMobileOverlay(query?.matches ?? false);
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (query) {
      sync();
      query.addEventListener('change', sync);
    }
    initialFocusRef.current?.focus();
    return () => {
      query?.removeEventListener('change', sync);
      returnFocusRef.current?.focus();
    };
  }, [active, initialFocusRef]);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !isMobileOverlay) return;
    const focusable = [...(paneRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [])]
      .filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, [isMobileOverlay, onClose]);

  return { paneRef, isMobileOverlay, onKeyDown };
}
